/**
 * Accounts Receivable shared types
 */

import { z } from "zod";
import { AR_STATEMENT_RECIPIENT_ROLE_MODES } from "./ProductModules";

/**
 * Source of a sales order - where it originated from
 */
export enum SalesOrderSource {
  Edi = "edi",
  Email = "email",
  DirectEntry = "direct_entry",
  FileUpload = "file_upload",
  ErpSync = "erp_sync",
}

/**
 * Status of a staged sales order (draft/review state)
 * These are orders that have been received but not yet approved
 */
export enum StagedPurchaseOrderStatus {
  RECEIVED = "RECEIVED",
  // PENDING_REVIEW = "PENDING_REVIEW",
  APPROVED = "APPROVED",
  REJECTED = "REJECTED",
  PENDING_CHANGES = "PENDING_CHANGES",
  /**
   * Auto-approval ran but the order could not be created in the ERP (Famous).
   * The approval transaction rolled back — no SalesOrder exists and the order
   * is NOT approved — so this is a visible, retryable state, not a terminal
   * one. Re-approving re-runs the full create+ERP-insert atomically; it is
   * never a "resync" of an already-created order. Reached only from the EDI
   * auto-approval path (a background job with no user watching); the manual
   * and workflow approval paths surface the same failure live to the caller.
   */
  ERP_SYNC_FAILED = "ERP_SYNC_FAILED",
}

/**
 * Lifecycle stage of a sales order as it moves through the approval funnel,
 * from inbound receipt to ERP submission. Persisted as discrete transition
 * events in `ar_order_stage_events` (one row per stage entry per staged PO).
 *
 * An order may SKIP the discount and/or order-approval stages (no discounts
 * applied, or no order approval required). The AWAITING_* stages can re-enter
 * (e.g. after an order approver requests changes). RECEIVED, SUBMITTED_TO_ERP
 * and REJECTED are once-only terminal-ish markers.
 *
 * SUBMITTED_TO_ERP is intentionally ERP-agnostic — it covers Oracle Fusion,
 * Oracle EBS and Famous (whichever ERP the org syncs to).
 */
export enum OrderStage {
  Received = "RECEIVED",
  AwaitingDiscountApproval = "AWAITING_DISCOUNT_APPROVAL",
  AwaitingOrderApproval = "AWAITING_ORDER_APPROVAL",
  SubmittedToErp = "SUBMITTED_TO_ERP",
  Rejected = "REJECTED",
}

/**
 * Coarse, v3-native lifecycle position of a draft sales order, rolled up for
 * the review-page stepper and the list badge. Derived purely from the v3 draft
 * (its `status`, `salesOrderId`, and recorded approval decisions in
 * `rawData.approvals`) — NOT the legacy `ar_order_stage_events` funnel, which
 * is keyed by the staged-PO id (a different model the v3 path does not use).
 *
 * `rejected` and `superseded` are branches off the happy path; the rest are
 * sequential: `draft → creating_discounts → approving → ready → created`.
 *
 * `creating_discounts` is the stage an order sits in when it has line(s) priced
 * below list with no discount created yet — the reviewer's next step is to
 * create the discounts that the order needs before it can move into approval.
 *
 * `superseded` means a revised PO arrived and replaced this draft with a newer
 * one (`supersededByDraftId` is set) before it was submitted to the ERP.
 *
 * `under_cs_review` is the terminal exception branch: every line was left off
 * the ERP order (inactive in the ERP, unmatched, or pulled by hand), so nothing
 * reached the ERP and Customer Service owns the order from here. Distributor
 * Managers have nothing left to do on it.
 */
export type OrderLifecycleStage =
  | "draft"
  | "creating_discounts"
  | "approving"
  | "ready"
  | "created"
  | "under_cs_review"
  | "rejected"
  | "superseded";

/**
 * Happy-path stepper sequence (rejected/superseded/under_cs_review are
 * branches, not steps).
 */
export const ORDER_LIFECYCLE_STAGE_SEQUENCE: OrderLifecycleStage[] = [
  "draft",
  "creating_discounts",
  "approving",
  "ready",
  "created",
];

export const ORDER_LIFECYCLE_STAGE_LABELS: Record<OrderLifecycleStage, string> =
  {
    draft: "Draft",
    creating_discounts: "Resolving Discounts",
    approving: "Approving",
    ready: "Ready",
    created: "Submitted",
    under_cs_review: "Under CS Review",
    rejected: "Rejected",
    superseded: "Superseded",
  };

/**
 * The slice of a recorded approval-gate decision (`rawData.approvals[key]`)
 * the lifecycle rollup reads. `auto`, `decidedBy` and `comment` only matter
 * for telling an auto-passed gate apart from a human decision — see
 * {@link isAutoApprovedDecision}.
 */
export interface LifecycleApprovalDecision {
  decision: string;
  decidedBy?: string | null;
  comment?: string | null;
  /** Set by the workflow when the gate auto-passed (`appliesWhen` miss). */
  auto?: boolean;
}

/** Marker comment written on auto-passed gates before the `auto` flag existed. */
const AUTO_APPROVED_COMMENT_PREFIX = "Auto-approved";

/**
 * True when an "approved" decision was recorded by the workflow itself because
 * the gate's `appliesWhen` condition didn't match the order — nobody decided
 * anything. Such a gate counts as approved for the all-approved → `ready`
 * promotion, but it is NOT a terminal human decision and must not pull the
 * order into `approving` (the reviewer's next step may still be to create the
 * missing discounts). The comment-prefix fallback recognises decisions written
 * before the explicit `auto` flag.
 */
export function isAutoApprovedDecision(d: LifecycleApprovalDecision): boolean {
  if (d.decision !== "approved") return false;
  if (d.auto === true) return true;
  return (
    d.decidedBy == null &&
    typeof d.comment === "string" &&
    d.comment.startsWith(AUTO_APPROVED_COMMENT_PREFIX)
  );
}

export interface LifecycleStageInput {
  /** The draft's `StagedPurchaseOrderStatus` value. */
  status: string;
  /** Set once the order has been created in the ERP. */
  salesOrderId?: string | null;
  /**
   * True when a registered ERP submission API submitted this draft (a
   * `submitted` row in ar_draft_sales_order_submissions) — the record-based
   * path creates no SalesOrder, so this is its `created` driver.
   */
  erpSubmitted?: boolean;
  /** Set once a revised PO replaced this draft (see `supersedeDraftSalesOrder`). */
  supersededByDraftId?: string | null;
  /**
   * True when every line on the draft is excluded from the ERP order (its
   * `approvalIssue.excludedFromOrder` stamp, written at submission). Surfaces
   * the `under_cs_review` exception stage on an APPROVED order.
   */
  allLinesExcluded?: boolean;
  /**
   * Recorded approval-gate decisions from `rawData.approvals`, if loaded.
   * Includes `"pending"` markers written when an event-driven gate raises its
   * task (see `recordPendingApproval`), so terminal decisions must be filtered
   * from pending ones when they matter.
   */
  approvalDecisions?: ReadonlyArray<LifecycleApprovalDecision>;
  /**
   * True when the order has an approval gate actively awaiting a decision
   * (a blocking run in review, or an active approval node with no decision).
   * Lets the stage show `approving` even before any decision is recorded.
   */
  hasActiveApproval?: boolean;
  /**
   * True when a line carries a discount that's pending approval (the spec's
   * "awaiting discount approval" funnel state). Also surfaces as `approving`.
   */
  hasPendingDiscountApproval?: boolean;
  /**
   * True when the order has line(s) priced below list with no discount created
   * yet — i.e. the order still needs discounts before it can move to approval.
   * Surfaces the `creating_discounts` stage. Distinct from
   * `hasPendingDiscountApproval` (a discount exists and is awaiting approval).
   */
  needsDiscounts?: boolean;
  /**
   * True when the approval chain still has a gate with no recorded decision —
   * including gates that haven't raised their task yet (event-driven chains
   * decide one gate per run, so between "gate N decided" and "gate N+1's task
   * raised" the decision bag looks all-approved while the chain is mid-flight).
   * When set, the all-approved → `ready` promotion is blocked: the order stays
   * `approving` until every gate has decided. Callers that can't compute the
   * gate census (no workflow state loaded) leave it undefined, keeping the
   * legacy decisions-only behavior.
   */
  hasUndecidedApprovalGate?: boolean;
}

/**
 * The stages decided by the draft's outcome alone, before approvals matter:
 * the CS-review exception, in the ERP, or replaced by a revision. `null` when
 * the order is still in the approval funnel.
 */
function outcomeStage(input: LifecycleStageInput): OrderLifecycleStage | null {
  // Every line is an exception item: nothing the ERP could take, so the order
  // is with Customer Service. Exclusion stamps are re-evaluated on each
  // submission attempt, so a fixed-and-resubmitted order drops out of this.
  // Only an APPROVED order (the exception-only submission approves it): an
  // explicit rejection wins, and a revised PO that replaced it moves the work
  // to the revision instead.
  if (
    input.allLinesExcluded &&
    input.status === StagedPurchaseOrderStatus.APPROVED &&
    !input.supersededByDraftId
  ) {
    return "under_cs_review";
  }
  // Submitted through a registered (schema-mapped) ERP submission API: no
  // SalesOrder row exists (submission records own submission), but the order
  // IS in the ERP — same terminal stage as the adapter path.
  if (input.salesOrderId || input.erpSubmitted) return "created";
  if (input.supersededByDraftId) return "superseded";
  return null;
}

/**
 * Roll a v3 draft up to a single {@link OrderLifecycleStage}. Pure — shared by
 * the backend list/detail handlers and the frontend stepper so they never
 * disagree. Precedence:
 * under_cs_review (approved, not superseded) → created → superseded → rejected →
 * ready → approving → creating_discounts → draft.
 * (A submitted draft is never superseded by construction, so `created` first is
 * defensive only.)
 */
export function rollupLifecycleStage(
  input: LifecycleStageInput,
): OrderLifecycleStage {
  const outcome = outcomeStage(input);
  if (outcome) return outcome;
  const decisions = input.approvalDecisions ?? [];
  const anyRejected = decisions.some((d) => d.decision === "rejected");
  if (input.status === StagedPurchaseOrderStatus.REJECTED || anyRejected) {
    return "rejected";
  }
  if (input.status === StagedPurchaseOrderStatus.APPROVED) return "ready";
  // Every raised gate decided "approved" and nothing else is pending: the
  // order is past approval — surface `ready` even before the status column
  // flips (it only flips on a successful ERP submission, so a failed
  // submission would otherwise leave the stepper stuck on Approving).
  const allApproved =
    decisions.length > 0 && decisions.every((d) => d.decision === "approved");
  if (
    allApproved &&
    !input.hasActiveApproval &&
    !input.hasUndecidedApprovalGate &&
    !input.hasPendingDiscountApproval &&
    !input.needsDiscounts
  ) {
    return "ready";
  }
  // In approval: a gate is actively awaiting a decision, a discount is pending
  // approval, or at least one gate DECIDED but the order isn't fully approved.
  // A pending-only marker (event-driven gate raised its task at intake, nobody
  // decided yet) deliberately does NOT outrank `creating_discounts`: the
  // reviewer's next step is still to create the missing discounts. Neither
  // does an auto-passed conditional gate — the workflow recorded it, no human
  // decided, so a chain of [auto-approved, pending] is still pre-approval.
  const terminalDecisions = decisions.filter(
    (d) => d.decision !== "pending" && !isAutoApprovedDecision(d),
  );
  if (
    input.hasActiveApproval ||
    input.hasPendingDiscountApproval ||
    terminalDecisions.length > 0
  ) {
    return "approving";
  }
  // Pre-approval but the order still needs discounts created (below-list lines
  // with no discount yet) — the reviewer's next step before approval.
  if (input.needsDiscounts) return "creating_discounts";
  // No discounts needed and a raised-but-undecided gate: awaiting approval.
  if (decisions.length > 0) return "approving";
  return "draft";
}

// ---------------------------------------------------------------------------
// AR Tasks
// ---------------------------------------------------------------------------

/**
 * The task namespace that holds Accounts Receivable work queues.
 * The AR summary dashboard filters the main task inbox down to this namespace,
 * and the scheduled AR scan workflows find-or-create it before filing tasks.
 * Shared here so backend task creation and frontend filtering can never drift.
 */
export const AR_TASK_NAMESPACE_KEY = "AR";
export const AR_TASK_NAMESPACE_NAME = "Accounts Receivable";

/**
 * Key inside `Task.metadata` under which AR workflow tasks store their action
 * payload. The AR summary dashboard parses this to attach an inline action
 * (e.g. opening the bulk statement send dialog pre-populated).
 */
export const AR_TASK_ACTION_METADATA_KEY = "arAction";

/** One customer queued into a bulk statement send task. */
export const arStatementSendTaskCustomerSchema = z.object({
  customerId: z.uuid(),
  customerName: z.string().nullable(),
  /** Decimal string; total open balance across the qualifying invoices. */
  openBalance: z.string().nullable(),
  /** ISO datetime of the customer's most recent statement send, if any. */
  lastStatementSentAt: z.iso.datetime().nullable(),
});
export type ArStatementSendTaskCustomer = z.infer<
  typeof arStatementSendTaskCustomerSchema
>;

/**
 * Action payload for a payment-reminder scan task: the customers to queue into
 * the bulk statement send dialog, plus the scan filters that produced them
 * (so the dialog can mirror the same aging basis and day range).
 */
export const arBulkStatementSendActionSchema = z.object({
  kind: z.literal("ar_bulk_statement_send"),
  customers: z.array(arStatementSendTaskCustomerSchema),
  /** Invoices strictly older than this many days qualified the customer. */
  invoiceAgeDays: z.number().int(),
  /** Customers sent a statement within this many days were skipped. */
  reminderCooldownDays: z.number().int(),
  /**
   * Invoices at least this many days old are included in the reminder
   * content. Independent of `invoiceAgeDays`; 0 means ALL outstanding
   * invoices. Defaults to 0 so `ar_bulk_statement_send` payloads persisted
   * before this field existed still parse (0 = today's "all outstanding"
   * behavior).
   */
  contentMinInvoiceAgeDays: z.number().int().nonnegative().default(0),
  /** Aging basis the scan used (mirrors the custom aging report basis). */
  agingBasis: z.string(),
  /**
   * Per-workflow override for how statement recipients are split across To and
   * Cc, winning over the org-level AR setting for statements opened from this
   * task. Optional rather than defaulted: omitting it means "use the org
   * default", which is also how payloads persisted before this field existed
   * must behave.
   */
  statementRecipientRoles: z.enum(AR_STATEMENT_RECIPIENT_ROLE_MODES).optional(),
  generatedAt: z.iso.datetime(),
});
export type ArBulkStatementSendAction = z.infer<
  typeof arBulkStatementSendActionSchema
>;

/** One created-but-never-sent invoice queued into an invoice send task. */
export const arInvoiceSendTaskInvoiceSchema = z.object({
  invoiceId: z.uuid(),
  invoiceNumber: z.string().nullable(),
  customerId: z.uuid(),
  customerName: z.string().nullable(),
  /** DATEONLY invoice date. */
  invoiceDate: z.string().nullable(),
  /** Decimal string; the invoice's remaining balance. */
  balanceDue: z.string().nullable(),
});
export type ArInvoiceSendTaskInvoice = z.infer<
  typeof arInvoiceSendTaskInvoiceSchema
>;

/**
 * Action payload for an invoice-send scan task: open invoices with no send
 * event recorded (neither a Gently email send nor a Famous-side send event).
 * The dashboard action opens the bulk invoice send dialog with these invoices.
 */
export const arInvoiceSendActionSchema = z.object({
  kind: z.literal("ar_invoice_send"),
  invoices: z.array(arInvoiceSendTaskInvoiceSchema),
  /** Invoices created at least this many hours ago qualified. */
  invoiceMinAgeHours: z.number().int(),
  generatedAt: z.iso.datetime(),
});
export type ArInvoiceSendAction = z.infer<typeof arInvoiceSendActionSchema>;

/**
 * Action payload for an agent-conversation reply task: a customer replied to
 * an email the agent is tracking on a collection thread, a suggested response
 * draft is waiting, and a human should review + send it. One task per
 * (conversation, inbound reply).
 */
export const arConversationReplyReviewActionSchema = z.object({
  kind: z.literal("ar_conversation_reply_review"),
  threadId: z.uuid(),
  /** Human-readable thread code, e.g. COL-46. */
  threadReferenceCode: z.string(),
  /** The AI-suggested reply draft; null when drafting failed. */
  draftId: z.uuid().nullable(),
  /** The customer reply that was filed onto the thread. */
  inboundEmailId: z.uuid(),
  agentConversationId: z.uuid(),
  generatedAt: z.iso.datetime(),
});
export type ArConversationReplyReviewAction = z.infer<
  typeof arConversationReplyReviewActionSchema
>;

/**
 * Action payload for an agent-conversation no-response task: an email the
 * agent is tracking on a collection thread has gone unanswered past the
 * configured window, and a human should nudge or escalate.
 */
export const arConversationNoResponseActionSchema = z.object({
  kind: z.literal("ar_conversation_no_response"),
  threadId: z.uuid(),
  /** Human-readable thread code, e.g. COL-46. */
  threadReferenceCode: z.string(),
  /** The unanswered outbound email. */
  outboundEmailId: z.uuid(),
  /** When the unanswered email went out (ISO); null if unknown. */
  sentAt: z.iso.datetime().nullable(),
  /** Whole days elapsed since the send when the scan flagged it. */
  daysWaiting: z.number().int().nonnegative(),
  counterpartyEmail: z.string().nullable(),
  agentConversationId: z.uuid(),
  generatedAt: z.iso.datetime(),
});
export type ArConversationNoResponseAction = z.infer<
  typeof arConversationNoResponseActionSchema
>;

/**
 * Union of AR task action payloads. Extended by each workflow that files
 * actionable AR tasks.
 */
export const arTaskActionSchema = z.discriminatedUnion("kind", [
  arBulkStatementSendActionSchema,
  arInvoiceSendActionSchema,
  arConversationReplyReviewActionSchema,
  arConversationNoResponseActionSchema,
]);
export type ArTaskAction = z.infer<typeof arTaskActionSchema>;

/**
 * Parse the AR action payload out of a task's metadata blob. Returns null for
 * tasks without one (or with an unrecognized/malformed payload) so callers can
 * fall back to the generic task rendering.
 */
export function parseArTaskAction(
  metadata: Record<string, unknown> | null | undefined,
): ArTaskAction | null {
  if (!metadata) return null;
  const result = arTaskActionSchema.safeParse(
    metadata[AR_TASK_ACTION_METADATA_KEY],
  );
  return result.success ? result.data : null;
}

// ---------------------------------------------------------------------------
// Pending Change Types (860 PO Change Request for approved orders)
// ---------------------------------------------------------------------------

export type PendingChangeDecision = "pending" | "approved" | "denied";
export type PendingChangeType = "update" | "delete" | "add";

export interface PendingLineItemChange {
  /** Index into the current lineItems array (-1 for adds) */
  originalLineItemIndex: number;
  changeType: PendingChangeType;
  /** Snapshot of original values before the change (null for adds) */
  originalValues: Record<string, unknown> | null;
  /** Proposed new values from the 860 */
  proposedValues: Record<string, unknown>;
  /** User decision */
  decision: PendingChangeDecision;
  /** Index into orderful860Payloads for traceability */
  edi860PayloadIndex: number;
}

export interface PendingHeaderChange {
  /** Field name on the staged purchase order (e.g. "deliveryDate") */
  field: string;
  /** Human-readable label */
  label: string;
  /** Current value */
  originalValue: unknown;
  /** Proposed new value from the 860 */
  proposedValue: unknown;
  /** User decision */
  decision: PendingChangeDecision;
  /** Index into orderful860Payloads for traceability */
  edi860PayloadIndex: number;
}

/**
 * Remittance verification result.
 *
 * The persisted outcome of the AI overseer (`verify_step_output`) checking an
 * extracted remittance advice against its source document. Stored as JSONB on
 * `ar_remittance_advice.verification_result` and surfaced on the remittance
 * detail page. Canonical here (lowest-level package) so the DB model, the
 * persist step handler, and the RPC contract all share one shape rather than
 * redeclaring it. Mirrors the generic step's output plus a `verifiedAt` stamp.
 */
export const REMITTANCE_VERIFICATION_VERDICTS = [
  "pass",
  "needs_review",
] as const;
export type RemittanceVerificationVerdict =
  (typeof REMITTANCE_VERIFICATION_VERDICTS)[number];

export const REMITTANCE_VERIFICATION_SEVERITIES = [
  "info",
  "warning",
  "critical",
] as const;
export type RemittanceVerificationSeverity =
  (typeof REMITTANCE_VERIFICATION_SEVERITIES)[number];

export const remittanceVerificationFindingSchema = z.object({
  severity: z.enum(REMITTANCE_VERIFICATION_SEVERITIES),
  title: z.string(),
  detail: z.string(),
  /** Pointer to the offending item (e.g. an invoice ref) for highlighting;
   * null when the finding isn't item-specific. Nullable (not optional) to mirror
   * the verify_step_output contract, which must satisfy OpenAI strict schemas. */
  reference: z.string().nullable(),
});
export type RemittanceVerificationFinding = z.infer<
  typeof remittanceVerificationFindingSchema
>;

export const remittanceVerificationResultSchema = z.object({
  verdict: z.enum(REMITTANCE_VERIFICATION_VERDICTS),
  summary: z.string(),
  findings: z.array(remittanceVerificationFindingSchema),
  /** Id of the escalation task opened on `needs_review`, else null. */
  taskId: z.string().nullable(),
  /** ISO-8601 timestamp of when the check ran. */
  verifiedAt: z.iso.datetime(),
});
export type RemittanceVerificationResult = z.infer<
  typeof remittanceVerificationResultSchema
>;

/**
 * Default extraction instructions for a backup document an operator downloaded
 * from a customer's vendor portal.
 *
 * The OCR service's own default is "analyze this document and extract all
 * relevant information", which returns a paragraph of prose. Two LLMs read this
 * text back — the collection-thread recommendation and the document-naming
 * agent — and both need the same specific things: the identifiers that tie the
 * document to an invoice, and the delivery facts that decide whether a
 * short-pay is defensible.
 *
 * Handwriting gets its own instruction because that is usually where the reason
 * for the deduction is: a receiver's scrawled shortage count or a damage note in
 * the margin, which a summarizing model tends to drop.
 *
 * Overridable per organization via `ar.collections.portalBackupExtraction
 * Instructions` — portals differ enough in what they serve that a generic list
 * leaves value on the table. Shared here (not held backend-side) so the settings
 * UI can seed the editor with the exact text the extraction would otherwise use,
 * rather than presenting a blank box.
 */
export const AR_DEFAULT_PORTAL_BACKUP_EXTRACTION_INSTRUCTIONS = `Extract the contents of this accounts-receivable backup document, which was downloaded from a customer's vendor portal to support or dispute a deduction.

Capture, where present:
- documentKind: what this is in plain words (proof of delivery, bill of lading, invoice, receiving report, claim/deduction detail, remittance advice, ...).
- identifiers: every reference number shown, each labelled as it is on the page — purchase order, invoice, sales order, deduction/claim, BOL/pro, tracking, check, store or DC number.
- parties: shipper, consignee, carrier, and the ship-to location or store.
- dates: ship date, delivery date, invoice date, received date — keep the format shown.
- lineItems: item code/description, quantity ordered, quantity shipped, quantity received, unit price, extended amount.
- amounts: any totals, deduction amounts, or adjustments, with the label beside each.
- deliveryEvidence: whether the document is signed, who signed it, the printed name and date/time beside the signature, and any stamp.
- exceptions: shortages, overages, damage, refusals, OS&D notes, temperature readings — the quantities and reasons as written.
- handwrittenNotes: transcribe handwritten or stamped annotations verbatim, including marginal notes and corrections. These carry the receiver's reason for a deduction and must not be summarized away.
- otherText: anything else on the page that a collector disputing this deduction would want to see.`;

/**
 * Appended to the extraction instructions above — the configured ones as much as
 * the default. Not overridable: the extracted text feeds a recommendation a
 * collector acts on, so an invented purchase-order number is worse than a null,
 * and that has to hold no matter how an org rewrites the field list.
 */
export const AR_PORTAL_BACKUP_EXTRACTION_GUARD = `Report ONLY what actually appears on the document. Use null for anything absent — never infer, complete, or guess a value.`;
